import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Calendar, MapPin, ScanLine, UserPlus, Sparkles, Trophy, X, ExternalLink, PlayCircle, CheckCircle2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import UserAvatar from '../components/UserAvatar';
import QrScanBox from '../components/QrScanBox';

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function EventDetailPage() {
  const { eventId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const quickFlow = params.get('quick') === '1';
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // 'username' | 'qr' | null
  const [uname, setUname] = useState('');
  const [busy, setBusy] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [winnersInput, setWinnersInput] = useState('');
  const [showWinners, setShowWinners] = useState(false);
  const [showBattleForm, setShowBattleForm] = useState(false);
  const [teamAInput, setTeamAInput] = useState('');
  const [teamBInput, setTeamBInput] = useState('');
  const [stake, setStake] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/events/${eventId}`);
      setEvent(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Evento no encontrado');
      nav('/events');
    } finally { setLoading(false); }
  }, [eventId, nav]);

  useEffect(() => { load(); }, [load]);

  // In quick-battle flow, auto-open QR scanner to add contestants ASAP
  useEffect(() => {
    if (quickFlow && event && (event.contestants_count || 0) < 2 && event.status !== 'finished' && event.status !== 'cancelled') {
      setMode((prev) => prev || 'qr');
    }
  }, [quickFlow, event]);

  const canManage = useMemo(() => {
    if (!event || !user) return false;
    if ((user.roles || []).includes('admin')) return true;
    return event.organizer?.user_id === user.user_id;
  }, [event, user]);

  const addByUsername = async () => {
    if (!uname.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${eventId}/contestants`, { username: uname.trim() });
      toast.success(`@${data.user?.username || uname} agregado`);
      setUname(''); setMode(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo agregar');
    } finally { setBusy(false); }
  };

  const addByQr = async (token) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${eventId}/contestants`, { qr_token: token });
      toast.success(`@${data.user?.username} agregado`);
      setMode(null); setScanError(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'QR inválido');
      setMode(null);
    } finally { setBusy(false); }
  };

  const removeContestant = async (username) => {
    if (!window.confirm(`¿Retirar a @${username}?`)) return;
    try {
      await api.delete(`/events/${eventId}/contestants/${username}`);
      toast.success('Contendiente retirado');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  const startBattle = async () => {
    // Parse comma-separated usernames or fall back to first two contestants
    const parse = (s) => s.split(',').map((x) => x.trim().replace(/^@/, '')).filter(Boolean);
    const contestants = event.contestants || [];
    const teamA = teamAInput ? parse(teamAInput) : (contestants[0] ? [contestants[0].user?.username] : []);
    const teamB = teamBInput ? parse(teamBInput) : (contestants[1] ? [contestants[1].user?.username] : []);
    if (!teamA.length || !teamB.length) { toast.error('Cada equipo necesita al menos un miembro'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${eventId}/battles`, {
        team_a_usernames: teamA,
        team_b_usernames: teamB,
        stake_amount: Math.max(0, parseInt(stake, 10) || 0),
      });
      toast.success(`Batalla ${data.format} creada${data.stake_amount ? ` · ${data.stake_amount} apostados/persona` : ''}`);
      setShowBattleForm(false); setTeamAInput(''); setTeamBInput(''); setStake(0);
      nav(`/battle/${data.battle_id || data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la batalla');
    } finally { setBusy(false); }
  };

  const declareWinners = async () => {
    const names = winnersInput.split(',').map((x) => x.trim()).filter(Boolean);
    if (!names.length) { toast.error('Escribe al menos un ganador'); return; }
    setBusy(true);
    try {
      await api.post(`/events/${eventId}/declare-winners`, { winner_usernames: names });
      toast.success('Ganadores declarados');
      setShowWinners(false); setWinnersInput('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/events/${eventId}`, { status });
      toast.success(`Estado: ${status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="min-h-screen bg-aura-bg grid place-items-center text-4xl text-aura-textDim animate-aura-pulse">⚡</div>;
  if (!event) return null;

  return (
    <div className="min-h-screen bg-aura-bg pb-28 relative">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <header className="relative flex items-center justify-between px-5 pt-6">
        <button onClick={() => nav(-1)} className="text-aura-textDim flex items-center gap-1" data-testid="btn-back">
          <ChevronLeft size={20} />
        </button>
        <div className="font-mono text-xs tracking-[0.25em] text-aura-cyan" data-testid="event-number">
          #EVT-{String(event.event_number).padStart(4, '0')}
        </div>
        <span className="w-8" />
      </header>

      <main className="relative mx-auto max-w-md px-5 pt-4 space-y-4">
        {event.type === 'quick' && (
          <div className="aura-card p-4 border-aura-pink/40 bg-gradient-to-r from-aura-pink/10 to-aura-cyan/10" data-testid="quick-banner">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-xl bg-aura-gradient text-black grid place-items-center shrink-0 shadow-neon-cyan">
                <Zap size={18} strokeWidth={2.6} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.3em] font-display font-black text-aura-pink">BATALLA INSTANTÁNEA</div>
                <div className="font-display font-black text-white">Escanea el QR de cada contendiente para registrarlo</div>
                <div className="text-xs text-aura-textDim mt-1">Cuando tengas al menos 2 contendientes, crea la Batalla 1 (puede ser 1v1, 2v2, 3v3…).</div>
              </div>
            </div>
          </div>
        )}

        {/* Header card */}
        <section className="aura-card p-5">
          <div className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase font-display font-bold ${
            event.status === 'live' ? 'bg-aura-pink/20 text-aura-pink' :
            event.status === 'scheduled' ? 'bg-aura-cyan/20 text-aura-cyan' :
            event.status === 'finished' ? 'bg-white/10 text-white/70' :
            'bg-red-500/20 text-red-400'
          }`} data-testid="event-status">{event.status}</div>
          <h1 className="mt-2 font-display font-black text-white text-2xl" data-testid="event-title">{event.title}</h1>
          {event.description && <p className="mt-1 text-sm text-aura-textDim">{event.description}</p>}
          <div className="mt-3 space-y-1 text-sm">
            <div className="flex items-center gap-2 text-white/80"><Calendar size={14} className="text-aura-cyan" /> {fmtDate(event.event_datetime)}</div>
            <div className="flex items-center gap-2 text-white/80"><MapPin size={14} className="text-aura-cyan" /> {event.venue_name ? `${event.venue_name} · ` : ''}{event.address}, {event.city} {event.state ? `· ${event.state}` : ''} · {event.country}</div>
          </div>
          {event.organizer?.username && (
            <div className="mt-4 flex items-center gap-2 border-t border-white/5 pt-3">
              <UserAvatar user={event.organizer} size="sm" />
              <div className="text-xs">
                <span className="text-aura-textDim">Organiza </span>
                <Link to={`/u/${event.organizer.username}`} className="text-white font-display font-semibold hover:text-aura-cyan">@{event.organizer.username}</Link>
              </div>
            </div>
          )}
          {event.maps_url && (
            <a href={event.maps_url} target="_blank" rel="noreferrer" className="aura-btn-ghost w-full mt-3 justify-center !py-2 !text-xs" data-testid="btn-open-maps">
              <ExternalLink size={12} /> Abrir en Google Maps
            </a>
          )}
        </section>

        {/* Winners */}
        {event.winners?.length > 0 && (
          <section className="aura-card p-4" data-testid="winners-section">
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={16} className="text-aura-gold" />
              <div className="font-display font-black text-aura-gold tracking-wider">GANADORES</div>
            </div>
            <div className="flex flex-wrap gap-2">
              {event.winners.map((w) => (
                <Link key={w.user_id} to={`/u/${w.username}`} className="flex items-center gap-2 bg-aura-gold/10 border border-aura-gold/30 rounded-full pl-1 pr-3 py-1">
                  <UserAvatar user={w} size="xs" />
                  <span className="text-white font-display font-semibold text-xs">@{w.username}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Contestants */}
        <section className="aura-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-black text-white tracking-wider text-sm">CONTENDIENTES ({event.contestants_count})</div>
            {canManage && event.status !== 'finished' && event.status !== 'cancelled' && (
              <div className="flex gap-1">
                <button data-testid="btn-add-username" onClick={() => { setMode('username'); setScanError(null); }} className="text-[10px] font-display font-bold uppercase px-2 py-1 rounded-full border border-aura-cyan/40 text-aura-cyan hover:bg-aura-cyan/10">
                  <UserPlus size={12} className="inline mr-1" /> User
                </button>
                <button data-testid="btn-add-qr" onClick={() => { setMode('qr'); setScanError(null); }} className="text-[10px] font-display font-bold uppercase px-2 py-1 rounded-full border border-aura-pink/40 text-aura-pink hover:bg-aura-pink/10">
                  <ScanLine size={12} className="inline mr-1" /> QR
                </button>
              </div>
            )}
          </div>

          {mode === 'username' && (
            <div className="mb-3 space-y-2" data-testid="add-username-form">
              <input
                data-testid="in-add-username"
                value={uname}
                onChange={(e) => setUname(e.target.value)}
                placeholder="@username"
                className="w-full bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-cyan/60"
              />
              <div className="flex gap-2">
                <button onClick={() => setMode(null)} className="aura-btn-ghost flex-1">Cancelar</button>
                <button data-testid="btn-confirm-username" onClick={addByUsername} disabled={busy} className="aura-btn-primary flex-1">
                  {busy ? '…' : 'Agregar'}
                </button>
              </div>
            </div>
          )}

          {mode === 'qr' && (
            <div className="mb-3 space-y-2" data-testid="add-qr-form">
              <div className="text-[11px] text-aura-textDim text-center">Apunta a la <span className="text-aura-cyan">Aura QR</span> del contendiente</div>
              {!scanError ? (
                <QrScanBox
                  active
                  onToken={addByQr}
                  onError={(m) => setScanError(m)}
                  testId="qr-scan-box-contestant"
                />
              ) : (
                <div className="aura-card p-3 text-center text-xs text-red-400">{scanError}</div>
              )}
              <button onClick={() => setMode(null)} className="aura-btn-ghost w-full">Cancelar</button>
            </div>
          )}

          {event.contestants.length === 0 ? (
            <div className="text-center text-xs text-aura-textDim py-6" data-testid="contestants-empty">Aún sin contendientes.</div>
          ) : (
            <ul className="space-y-2" data-testid="contestants-list">
              {event.contestants.map((c, i) => (
                <li key={c.user_id} className="flex items-center gap-3">
                  <div className="w-6 text-center font-mono text-aura-cyan text-xs">{i + 1}</div>
                  <UserAvatar user={c.user} size="sm" />
                  <Link to={`/u/${c.user?.username}`} className="flex-1 min-w-0">
                    <div className="text-white font-display font-semibold text-sm truncate">{c.user?.name}</div>
                    <div className="text-[11px] text-aura-textDim">@{c.user?.username}</div>
                  </Link>
                  <div className="text-right">
                    <div className="font-mono font-bold text-aura-cyan text-xs">{(c.user?.aura_points || 0).toLocaleString()}</div>
                    <div className="text-[9px] uppercase tracking-widest text-aura-textDim">Aura</div>
                  </div>
                  {canManage && event.status !== 'finished' && event.status !== 'cancelled' && (
                    <button onClick={() => removeContestant(c.user?.username)} data-testid={`btn-remove-${c.user?.username}`}
                      className="text-red-400 hover:text-red-300 p-1">
                      <X size={14} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Battles linked */}
        <section className="aura-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-black text-white tracking-wider text-sm">BATALLAS DEL EVENTO ({event.battles_count})</div>
            {canManage && event.status !== 'finished' && event.status !== 'cancelled' && event.contestants.length >= 2 && (
              <button onClick={() => setShowBattleForm((v) => !v)} disabled={busy} data-testid="btn-toggle-battle-form" className="text-[10px] font-display font-bold uppercase px-2 py-1 rounded-full border border-aura-cyan/40 text-aura-cyan hover:bg-aura-cyan/10">
                <PlayCircle size={12} className="inline mr-1" /> Nueva batalla
              </button>
            )}
          </div>

          {showBattleForm && (
            <div className="mb-3 space-y-2 border border-white/10 rounded-xl p-3 bg-white/[0.02]" data-testid="battle-form">
              <div className="text-[10px] text-aura-textDim uppercase tracking-widest">Formato libre — 1v1, 2v2, 3v3, etc.</div>
              <div className="space-y-2">
                <div>
                  <label className="text-[9px] uppercase tracking-widest font-display font-black text-aura-cyan">EQUIPO A</label>
                  <input
                    data-testid="in-team-a"
                    value={teamAInput}
                    onChange={(e) => setTeamAInput(e.target.value)}
                    placeholder="@user1, @user2 (separados por coma)"
                    className="w-full bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-cyan/60"
                  />
                </div>
                <div className="text-center font-display font-black text-aura-textDim">VS</div>
                <div>
                  <label className="text-[9px] uppercase tracking-widest font-display font-black text-aura-pink">EQUIPO B</label>
                  <input
                    data-testid="in-team-b"
                    value={teamBInput}
                    onChange={(e) => setTeamBInput(e.target.value)}
                    placeholder="@user3, @user4 (separados por coma)"
                    className="w-full bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-pink/60"
                  />
                </div>
                <div className="pt-2 border-t border-white/5">
                  <label className="text-[9px] uppercase tracking-widest font-display font-black text-aura-gold">APUESTA POR PERSONA (STAKE)</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      data-testid="in-stake"
                      type="number" min="0" step="10"
                      value={stake}
                      onChange={(e) => setStake(e.target.value)}
                      className="flex-1 bg-aura-surface2 border border-aura-gold/30 rounded-xl px-3 py-2 text-sm text-white font-mono outline-none focus:border-aura-gold"
                    />
                    <span className="text-aura-gold font-display font-black text-xs">AURA</span>
                  </div>
                  <div className="text-[10px] text-aura-textDim mt-1">Cada miembro apuesta esa cantidad. Ganadores se reparten el pot completo. 0 = sin apuesta.</div>
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => setShowBattleForm(false)} className="aura-btn-ghost flex-1">Cancelar</button>
                <button onClick={startBattle} disabled={busy} data-testid="btn-create-battle" className="aura-btn-primary flex-1">
                  {busy ? '…' : 'Crear batalla'}
                </button>
              </div>
              <div className="text-[10px] text-aura-textDim">
                Todos los usernames deben estar registrados como contendientes del evento.
              </div>
            </div>
          )}

          {event.battles.length === 0 ? (
            <div className="text-center text-xs text-aura-textDim py-4" data-testid="battles-empty">Sin batallas vinculadas todavía.</div>
          ) : (
            <ul className="space-y-2" data-testid="battles-list">
              {event.battles.map((b) => (
                <li key={b.battle_id}>
                  <Link to={`/battle/${b.battle_id}`} data-testid={`battle-link-${b.battle_id}`} className="block p-3 rounded-xl bg-aura-surface2 hover:border-aura-cyan/40 border border-transparent">
                    <div className="flex items-center justify-between text-[10px] font-mono">
                      <span className="text-aura-cyan tracking-[0.25em]">
                        {b.battle_number ? `#B-${String(b.battle_number).padStart(3, '0')}` : `#${b.battle_id.slice(-6)}`}
                        <span className="ml-2 text-white/60">· {b.format || '1v1'}</span>
                      </span>
                      <div className="flex items-center gap-2 text-[10px] font-display font-bold">
                        <span className={`px-2 py-0.5 rounded-full ${b.status === 'live' || b.status === 'voting' ? 'bg-aura-pink/20 text-aura-pink' : b.status === 'finished' || b.status === 'completed' ? 'bg-white/10 text-white/70' : 'bg-aura-cyan/20 text-aura-cyan'}`}>{b.status}</span>
                        <span className="text-aura-textDim">{b.total_votes} votos</span>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-white/80">
                      <span className={b.winning_side === 'a' ? 'text-aura-gold font-bold' : 'text-aura-cyan'}>{(b.team_a || []).map((u) => `@${u.username}`).join(', ')}</span>
                      <span className="mx-2 text-aura-textDim">vs</span>
                      <span className={b.winning_side === 'b' ? 'text-aura-gold font-bold' : 'text-aura-pink'}>{(b.team_b || []).map((u) => `@${u.username}`).join(', ')}</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Organizer controls */}
        {canManage && event.status !== 'finished' && event.status !== 'cancelled' && (
          <section className="aura-card p-4 space-y-2" data-testid="organizer-controls">
            <div className="font-display font-black text-white tracking-wider text-sm mb-2">CONTROLES DEL ORGANIZADOR</div>
            <div className="flex gap-2 flex-wrap">
              {event.status === 'scheduled' && (
                <button onClick={() => setStatus('live')} data-testid="btn-set-live" className="aura-btn-ghost !text-xs">Marcar EN VIVO</button>
              )}
              <button onClick={() => setShowWinners((v) => !v)} data-testid="btn-toggle-winners" className="aura-btn-ghost !text-xs">
                <Sparkles size={12} /> Declarar ganadores
              </button>
            </div>
            {showWinners && (
              <div className="pt-2 space-y-2">
                <input
                  data-testid="in-winners"
                  value={winnersInput}
                  onChange={(e) => setWinnersInput(e.target.value)}
                  placeholder="usernames separados por coma"
                  className="w-full bg-aura-surface2 border border-white/10 rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-aura-gold/60"
                />
                <button onClick={declareWinners} disabled={busy} data-testid="btn-declare-winners" className="aura-btn-primary w-full">
                  <CheckCircle2 size={14} /> Declarar y finalizar
                </button>
              </div>
            )}
          </section>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
